/**
 * Step 7: Withdrawal Strategy
 * Choose how to sequence withdrawals to minimize tax
 */

import { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Step8FormData, Step8Schema, CareScenarioItem, type AllStepFormData, type AnnuityPurchaseData } from '../../types/wizard';
import { AnnuityPurchaseSection } from './AnnuityPurchaseSection';
import { isFeatureEnabled } from '../../config/features';
import { replaceFirstAnnuityPurchase, useAnnuityBlockedState } from './annuityPurchaseHelpers';
import { useCurrencyInput } from '../../hooks/useCurrencyInput';
import { formatCurrency } from '../../services/chartDataTransformers';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP9_TOUR } from '../TourGuide/tourSteps';
import { TimeHorizonAllocationTable } from '../TimeHorizonAllocationTable';
import { blendedEquityPctFromHoldings } from '../../utils/allocation';
import { REBALANCING_CGT_CAVEAT, FIXED_ALLOCATION_BLEND_NOTE } from '../../constants/rebalancingCopy';
import './Step7Strategy.css';

interface Step7Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step8FormData) => void;
  onSave: (data: Step8FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

function Step7Strategy({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step7Props>) {
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<Step8FormData>({
    resolver: zodResolver(Step8Schema),
    // The only place initialData is read into the form — see Step6Assumptions
    // for why the mount reset() that used to duplicate this was removed.
    defaultValues: {
      decumulationStrategy: initialData?.decumulationStrategy || 'TAX_OPTIMISED',
      projectionYears: initialData?.projectionYears || 30,
      isaAnnualAllowance: initialData?.isaAnnualAllowance || 20000,
      isaShelteringMode: initialData?.isaShelteringMode || 'FULL',
      bandFillMode: initialData?.bandFillMode || 'PA_AND_BASIC',
      timeHorizonRebalancing: initialData?.timeHorizonRebalancing ?? false,
      // undefined (omission), never null — the server projection schema rejects
      // an explicit null (#1623 code-review + silent-failure). A loaded null also
      // gets cleaned to undefined here on the next save.
      allocationOverridePct: initialData?.allocationOverridePct ?? undefined,
      // Same rule: omission, never null (the server schema rejects null).
      emergencyFund: initialData?.emergencyFund ?? undefined,
      annuityPurchases: initialData?.annuityPurchases ?? undefined,
    },
  });



  const selectedStrategy = watch('decumulationStrategy');
  const watchedProjectionYears = watch('projectionYears');

  // Investment Rebalancing (moved into the wizard, #1623): Fixed Allocation vs
  // Time-Horizon, plus a Fixed-mode equity/bond slider that writes the same
  // allocationOverridePct the results panel does (0–1 equity, uniform, null =
  // use the mix derived from holdings).
  const timeHorizon = watch('timeHorizonRebalancing') ?? false;
  const allocationOverride = watch('allocationOverridePct'); // 0–1 | null | undefined
  const holdingsEquityPct = useMemo(
    () => blendedEquityPctFromHoldings(initialData?.holdings),
    [initialData?.holdings],
  );
  // Committed equity split: an explicit override wins, else the holdings mix, else 60%.
  const effectiveEquityPct = allocationOverride ?? holdingsEquityPct ?? 0.6;
  // Local slider position while dragging — the override is committed only on
  // release (#1623 ux-krug: an accidental nudge shouldn't replace "use my mix").
  const [sliderEquity, setSliderEquity] = useState<number | null>(null);
  const displayEquityPct = sliderEquity ?? effectiveEquityPct;

  const careConflicts = useMemo(() => {
    const scenarios: CareScenarioItem[] = initialData?.careScenarios || [];
    if (!watchedProjectionYears || scenarios.length === 0) return [];
    return scenarios.filter(s => s.startYear > watchedProjectionYears);
  }, [initialData?.careScenarios, watchedProjectionYears]);

  // Call currency hook at top level (same pattern as Step4Property)
  const isaAllowanceInput = useCurrencyInput({
    value: watch('isaAnnualAllowance'),
    registerProps: register('isaAnnualAllowance', { valueAsNumber: true }),
  });
  // A reserve above everything the household holds drives every withdrawal
  // rule to zero and marks every year breached; nothing else names the user's
  // own figure back to them.
  const householdTotal = useMemo(() => {
    const cash = initialData?.householdCash?.jointAccountBalance ?? 0;
    const holdings = Object.values(initialData?.holdings ?? {}).flat() as Array<{ currentValue?: number }>;
    return cash + holdings.reduce((sum, h) => sum + (h?.currentValue ?? 0), 0);
  }, [initialData?.householdCash?.jointAccountBalance, initialData?.holdings]);
  const watchedEmergencyFund = watch('emergencyFund');
  const emergencyFundExceedsHoldings = (watchedEmergencyFund ?? 0) > householdTotal && householdTotal > 0;

  const emergencyFundInput = useCurrencyInput({
    value: watch('emergencyFund'),
    registerProps: register('emergencyFund', { valueAsNumber: true }),
  });

  // Save form data after react-hook-form processes the onChange via setTimeout(0).
  // react-hook-form batches state updates in microtasks, so deferring to the macrotask
  // queue ensures getValues() returns the committed value. getValues is stable across renders.
  const timerRef = useRef<ReturnType<typeof setTimeout>>(0 as unknown as ReturnType<typeof setTimeout>);
  const saveAfterUpdate = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onSave(getValues());
    }, 0);
  }, [getValues, onSave]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const setRebalancing = useCallback((enabled: boolean) => {
    setValue('timeHorizonRebalancing', enabled, { shouldDirty: true });
    // Selecting Time-Horizon clears any Fixed-mode override, so a stale value
    // can't leak into the engine's dynamic-spending allocation (#1623 code-review 2).
    if (enabled) {
      setValue('allocationOverridePct', undefined, { shouldDirty: true });
      setSliderEquity(null);
    }
    saveAfterUpdate();
  }, [setValue, saveAfterUpdate]);
  const commitEquityOverride = useCallback(() => {
    if (sliderEquity != null) {
      setValue('allocationOverridePct', sliderEquity, { shouldDirty: true });
      setSliderEquity(null);
      saveAfterUpdate();
    }
  }, [sliderEquity, setValue, saveAfterUpdate]);
  const resetOverride = useCallback(() => {
    setValue('allocationOverridePct', undefined, { shouldDirty: true });
    setSliderEquity(null);
    saveAfterUpdate();
  }, [setValue, saveAfterUpdate]);

  const handleBlur = () => {
    // Always save on blur - isDirty doesn't work reliably with useCurrencyInput
    const data = watch();
    onSave(data);
  };

  // Annuity purchase, one per person. Held in the form value as the wizard's
  // record shape ({ person0: [...] }) so it reaches the API untouched.
  const persons = initialData?.persons ?? [];
  const annuityPersons = persons.length > 0 ? persons : [undefined];
  // A switch only stops NEW input: a plan that already holds the data (built
  // earlier, or through the API) keeps its inputs so the value can be changed or cleared.
  const showReserves = isFeatureEnabled('cashReserves') || (initialData?.emergencyFund ?? 0) > 0;
  const showAnnuity = isFeatureEnabled('annuityPurchase')
    || Object.values(initialData?.annuityPurchases ?? {}).some(rows => (rows?.length ?? 0) > 0);
  const isJointPlan = initialData?.planType === 'JOINT';
  const annuityPurchases = watch('annuityPurchases');
  // A purchase age below the person's minimum pension age is held out of form
  // state, so the resolver cannot see it — the step tracks it here instead and
  // refuses to advance, rather than letting it fail as a server error later.
  // Shared with the mobile twin so both gate identically.
  const { annuityBlocked, handleAnnuityValidity } = useAnnuityBlockedState();

  const setAnnuityPurchase = useCallback((personIndex: number, purchase: AnnuityPurchaseData | null) => {
    const current = getValues('annuityPurchases') ?? {};
    setValue('annuityPurchases', {
      ...current,
      // The wizard edits the FIRST purchase only, but a plan built through MCP
      // can hold several. Keep the rest untouched instead of overwriting the
      // array with a single element, which silently deleted them.
      [`person${personIndex}`]: replaceFirstAnnuityPurchase(current[`person${personIndex}`], purchase),
    }, { shouldDirty: true });
    saveAfterUpdate();
  }, [getValues, setValue, saveAfterUpdate]);

  const onSubmit = (data: Step8FormData) => {
    onNext(data);
  };

  const strategies = [
    {
      id: 'TAX_OPTIMISED' as const,
      icon: '',
      title: 'Tax-Optimised',
      badge: 'Popular choice',
      description: 'Minimize tax by intelligently sequencing withdrawals:',
      bullets: [
        'Take PCLS (25% tax-free lump sum) from SIPP when optimal',
        'Prioritize ISA withdrawals (tax-free)',
        'Keep SIPP taxable withdrawals below higher-rate threshold (£50,270 in 2024/25, inflation-adjusted)',
        'Factor in state pension as taxable income',
        'Direct property sale proceeds into ISA within annual allowance where possible',
        'Optimize PCLS timing across retirement phases',
      ],
      bestFor: 'Commonly selected to minimise income tax by sequencing withdrawals within lower tax bands. Trades some tax now for tax-free growth later — if a large pension can fund your spending entirely tax-free via phased tax-free cash (see "SIPP First"), that can outperform this strategy over shorter horizons.',
    },
    {
      id: 'ISA_FIRST' as const,
      icon: '',
      title: 'ISA First',
      badge: null,
      description: 'Withdraw tax-free ISA money first, then SIPP (with PCLS taken at retirement)',
      bullets: [],
      bestFor: 'Preserving SIPP growth or delaying SIPP crystallization',
    },
    {
      id: 'SIPP_FIRST' as const,
      icon: '',
      title: 'SIPP First',
      badge: null,
      description: 'Withdraw from SIPP first (with PCLS taken at retirement), then ISA',
      bullets: [],
      bestFor: 'Taking tax-free cash early, expecting higher tax rates later, or a large pension with little other income — phased tax-free cash can fund spending with little or no tax, which may beat Tax-Optimised over shorter retirements',
    },
    {
      id: 'PROPORTIONAL' as const,
      icon: '',
      title: 'Proportional',
      badge: null,
      description: 'Withdraw proportionally from all account types',
      bullets: [],
      bestFor: 'Balanced depletion of all accounts',
    },
  ];

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>How should we withdraw to meet your spending?</h2>
          <TourButton tour={WIZARD_STEP9_TOUR} variant="text" />
        </div>
        <p>Choose how to sequence withdrawals from your accounts to minimize tax</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown} className="step-form">
        {/* Strategy Cards */}
        <fieldset className="strategy-cards-grid">
          <legend className="sr-only">Decumulation Strategy</legend>
          {strategies.map((strategy) => (
            <label
              key={strategy.id}
              className={`strategy-card ${selectedStrategy === strategy.id ? 'selected' : ''}`}
            >
              <input
                type="radio"
                value={strategy.id}
                {...register('decumulationStrategy', {
                  onChange: saveAfterUpdate,
                })}
                className="strategy-radio"
              />
              <div className="strategy-header">
                <h3 className="strategy-title">{strategy.title}</h3>
                {strategy.badge && <span className="recommended-badge">{strategy.badge}</span>}
              </div>
              <p className="strategy-description">{strategy.description}</p>
              {strategy.bullets.length > 0 && (
                <ul className="strategy-bullets">
                  {strategy.bullets.map((bullet) => (
                    <li key={bullet.slice(0, 30)}>{bullet}</li>
                  ))}
                </ul>
              )}
              <p className="strategy-best-for">
                <strong>Best for:</strong> {strategy.bestFor}
              </p>
            </label>
          ))}
        </fieldset>

        {/* Investment Rebalancing */}
        <div className="rebalancing-fieldset">
          <h3 className="section-header">Investment Rebalancing</h3>
          <p className="help-text rebalancing-intro">
            How the model adjusts your portfolio allocation over time. Illustrative
            only — actual allocations depend on your fund choices and provider.
          </p>
          <fieldset className="rebalancing-options">
            <legend className="sr-only">Investment rebalancing strategy</legend>
            <label className={`rebalancing-option ${timeHorizon ? 'selected' : ''}`}>
              <input
                type="radio"
                name="investmentRebalancing"
                checked={timeHorizon}
                onChange={() => setRebalancing(true)}
              />
              <div>
                <strong>Time-Horizon</strong>
                <span className="option-description">Each wrapper gets its own equity/bond/cash mix based on how many years of spending it can fund</span>
              </div>
            </label>
            <label className={`rebalancing-option ${!timeHorizon ? 'selected' : ''}`}>
              <input
                type="radio"
                name="investmentRebalancing"
                checked={!timeHorizon}
                onChange={() => setRebalancing(false)}
              />
              <div>
                <strong>Fixed Allocation</strong>
                <span className="option-description">Keep a constant equity/bond split across all wrappers throughout the projection</span>
              </div>
            </label>
          </fieldset>

          {timeHorizon ? (
            <div className="rebalancing-detail">
              <TimeHorizonAllocationTable />
            </div>
          ) : (
            <div className="rebalancing-detail allocation-slider-group">
              <label htmlFor="allocationSlider" className="form-label">Equity / bond split</label>
              <input
                id="allocationSlider"
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round(displayEquityPct * 100)}
                onChange={(e) => setSliderEquity(Number(e.target.value) / 100)}
                onMouseUp={commitEquityOverride}
                onTouchEnd={commitEquityOverride}
                onKeyUp={commitEquityOverride}
                aria-label="Equity percentage"
              />
              <div className="allocation-slider-readout">
                <span>Equity {Math.round(displayEquityPct * 100)}%</span>
                <span>Bonds {Math.round((1 - displayEquityPct) * 100)}%</span>
              </div>
              {displayEquityPct < 0.3 && (
                <p className="rebalancing-warning">A low equity share may not keep pace with inflation over a long retirement.</p>
              )}
              {displayEquityPct > 0.9 && (
                <p className="rebalancing-warning">A high equity share raises short-term volatility — riskier if you&rsquo;re drawing down soon.</p>
              )}
              <span className="help-text">{FIXED_ALLOCATION_BLEND_NOTE}</span>
              {allocationOverride != null ? (
                <button
                  type="button"
                  className="allocation-btn allocation-btn--reset"
                  onClick={resetOverride}
                >
                  {holdingsEquityPct != null
                    ? `Reset to my holdings’ mix (${Math.round(holdingsEquityPct * 100)}% equity)`
                    : 'Reset to default (60% equity)'}
                </button>
              ) : (
                <span className="help-text">
                  {holdingsEquityPct != null
                    ? `Defaults to your holdings’ mix (${Math.round(holdingsEquityPct * 100)}% equity). Drag to override.`
                    : 'Defaults to 60% equity. Drag to override.'}
                </span>
              )}
            </div>
          )}
          <p className="help-text rebalancing-caveat">{REBALANCING_CGT_CAVEAT}</p>
        </div>

        {/* Cash reserves — a floor on household cash, not a separate pot */}
        {showReserves && (
        <div className="cash-reserves-fieldset">
          <h3 className="section-header">Cash reserves</h3>
          <div className="form-group">
            <label htmlFor="emergencyFund" className="form-label">
              Emergency fund to keep in cash (£, today&apos;s money)
            </label>
            <input
              id="emergencyFund"
              type="text"
              inputMode="numeric"
              className={`form-input ${errors.emergencyFund ? 'error' : ''}`}
              placeholder="e.g. 15,000"
              {...emergencyFundInput}
              onBlur={(e) => {
                emergencyFundInput.onBlur(e);
                handleBlur();
              }}
            />
            {errors.emergencyFund && (
              <span className="error-message">{errors.emergencyFund.message}</span>
            )}
            <span className="help-text">
              Kept in cash, not invested, and only used when everything else has run out
            </span>
            {emergencyFundExceedsHoldings && (
              <span className="error-message" role="alert">
                That is more than the {formatCurrency(householdTotal)} you hold in cash and investments.
                The reserve is a floor on your cash, so a target above everything you own leaves
                nothing for the plan to draw on.
              </span>
            )}
          </div>
        </div>
        )}

        {/* Annuity purchase */}
        {showAnnuity && (
        <div className="annuity-fieldset">
          <h3 className="section-header">Annuity purchase</h3>
          <p className="help-text annuity-intro">
            Swap part of the pension for a guaranteed income for life, keeping the rest in drawdown.
            Rates are indicative — get your own quote before deciding.
          </p>
          {annuityPersons.map((person, i) => (
            <AnnuityPurchaseSection
              key={person?.fullName || `annuity-person-${i}`}
              personIndex={i}
              person={person}
              showPersonName={annuityPersons.length > 1}
              isJointPlan={isJointPlan}
              purchase={annuityPurchases?.[`person${i}`]?.[0]}
              purchaseCount={annuityPurchases?.[`person${i}`]?.length}
              onChange={setAnnuityPurchase}
              onValidityChange={handleAnnuityValidity}
            />
          ))}
        </div>
        )}

        {/* Additional Settings */}
        <div className="additional-settings">
          <h3 className="section-header">Additional Settings</h3>

          <div className="form-group">
            <label htmlFor="projectionYears" className="form-label">
              Projection Years <span className="required">*</span>
            </label>
            <input
              id="projectionYears"
              type="number"
              min="10"
              max="100"
              className={`form-input ${errors.projectionYears ? 'error' : ''}`}
              {...register('projectionYears', { valueAsNumber: true })}
              onBlur={handleBlur}
            />
            {errors.projectionYears && (
              <span className="error-message">{errors.projectionYears.message}</span>
            )}
            <span className="help-text">How far to project (typically to age 100+)</span>
            {careConflicts.length > 0 && (
              <div className="validation-warning" role="alert">
                <strong>Care scenarios exceed projection period:</strong>
                <ul>
                  {careConflicts.map(s => (
                    <li key={s.scenarioId}>
                      {s.careType.charAt(0) + s.careType.slice(1).toLowerCase()} care starts year {s.startYear} (projection ends year {watchedProjectionYears})
                    </li>
                  ))}
                </ul>
                <span>Increase projection years or go back to Later Life & Estate and adjust care start years.</span>
              </div>
            )}
          </div>

          {selectedStrategy === 'TAX_OPTIMISED' && (
            <details className="strategy-advanced-disclosure">
              <summary className="strategy-advanced-summary">Advanced tax-optimised settings</summary>
            <fieldset className="form-group">
              <legend className="form-label">
                ISA Sheltering Mode
              </legend>
              <div className="isa-sheltering-options">
                <label className={`isa-sheltering-option ${watch('isaShelteringMode') === 'AEA_AWARE' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="AEA_AWARE"
                    {...register('isaShelteringMode', {
                      onChange: saveAfterUpdate,
                    })}
                  />
                  <div>
                    <strong>AEA-Aware</strong>
                    <span className="option-description">Limit sheltering to avoid CGT — conservative, avoids tax now</span>
                  </div>
                </label>
                <label className={`isa-sheltering-option ${watch('isaShelteringMode') === 'FULL' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="FULL"
                    {...register('isaShelteringMode', {
                      onChange: saveAfterUpdate,
                    })}
                  />
                  <div>
                    <strong>Full Shelter</strong>
                    <span className="option-description">Use full ISA allowance — pays CGT now for tax-free growth later</span>
                  </div>
                </label>
              </div>
              <span className="help-text">
                Controls how much brokerage is moved to ISA each year via bed-and-ISA
              </span>
            </fieldset>

            <fieldset className="form-group">
              <legend className="form-label">
                SIPP Band-Fill Mode
              </legend>
              <div className="isa-sheltering-options">
                <label className={`isa-sheltering-option ${watch('bandFillMode') === 'PA_AND_BASIC' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="PA_AND_BASIC"
                    {...register('bandFillMode', {
                      onChange: saveAfterUpdate,
                    })}
                  />
                  <div>
                    <strong>PA + Basic Rate</strong>
                    <span className="option-description">Fill PA + basic rate band — draws more SIPP now at 0-20%, reduces future higher-rate tax</span>
                  </div>
                </label>
                <label className={`isa-sheltering-option ${watch('bandFillMode') === 'PA_AND_HIGHER' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="PA_AND_HIGHER"
                    {...register('bandFillMode', {
                      onChange: saveAfterUpdate,
                    })}
                  />
                  <div>
                    <strong>PA + Basic + Higher Rate</strong>
                    <span className="option-description">Accelerated drawdown — pays 40% income tax but removes funds from IHT estate (beneficial when estate IHT exceeds income tax)</span>
                  </div>
                </label>
                <label className={`isa-sheltering-option ${watch('bandFillMode') === 'PA_ONLY' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="PA_ONLY"
                    {...register('bandFillMode', {
                      onChange: saveAfterUpdate,
                    })}
                  />
                  <div>
                    <strong>PA Only</strong>
                    <span className="option-description">Fill Personal Allowance only — minimal SIPP draw, zero tax cost</span>
                  </div>
                </label>
              </div>
              <span className="help-text">
                Controls how much SIPP is drawn each year to use tax-free allowances</span>

            </fieldset>
            </details>
          )}

          <div className="form-group">
            <label htmlFor="isaAnnualAllowance" className="form-label">
              ISA Annual Allowance per person (£) <span className="required">*</span>
            </label>
            <input
              id="isaAnnualAllowance"
              type="text"
              className={`form-input ${errors.isaAnnualAllowance ? 'error' : ''}`}
              {...isaAllowanceInput}
              onBlur={(e) => {
                isaAllowanceInput.onBlur(e);
                handleBlur();
              }}
            />
            {errors.isaAnnualAllowance && (
              <span className="error-message">{errors.isaAnnualAllowance.message}</span>
            )}
            <span className="help-text">
              £20,000 per person per year (UK annual limit). For joint plans, each person has their own separate allowance.
            </span>
          </div>

        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {onBack && (
            // Also gated: leaving with an unsaved, below-minimum age would
            // discard what the user typed with no indication it was dropped.
            <button type="button" className="btn btn-secondary" disabled={annuityBlocked} onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" disabled={careConflicts.length > 0 || annuityBlocked} onClick={() => handleSubmit(onSubmit)()}>
            Review &amp; Create Projection
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default Step7Strategy;
